import { System } from 'common/systems/domain/resources/system.resource';
import { SystemsState } from 'common/systems/domain/entities/systems-state';

/**
 * Pure, framework-agnostic by-id upsert of `System` updates into
 * `SystemsState`. For each `System` in `updatedSystems`: if its `id` is
 * not yet known, it's inserted as a new entity (also adding it to `ids`);
 * if its `id` is already present in `state.entities`, the existing
 * `System` instance is updated in place by calling
 * `.fill(updatedSystem.toObject())` on it, preserving that instance's
 * object identity while refreshing its attributes/relationships, instead
 * of replacing the stored reference. `state`'s `ids`/`entities`
 * containers are never mutated in place (new containers are returned),
 * and the `System` objects received in `updatedSystems` are never
 * mutated (see spec.md of `systems-capability`, Requirement "Pure by-id
 * merge of `System` updates").
 *
 * This in-place update is mechanism-and-content equivalent, for
 * already-present ids, to the legacy
 * `WebSocketSystemUpdaterService.updateSystems` (design.md Decision 3),
 * without sharing implementation with it: any external code holding a
 * direct reference to an existing `System` obtained from
 * `state.entities[id]` keeps seeing fresh data on that same reference
 * after a merge.
 */
export function mergeSystemUpdate(
    state: SystemsState,
    updatedSystems: ReadonlyArray<System>,
): SystemsState {
    const nextEntities: Record<string, System> = { ...state.entities };
    const newIds: Array<string> = [];

    updatedSystems.forEach((updatedSystem: System): void => {
        const existingSystem: System | undefined = nextEntities[updatedSystem.id];

        if (existingSystem) {
            existingSystem.fill(updatedSystem.toObject());
        } else {
            nextEntities[updatedSystem.id] = updatedSystem;
            newIds.push(updatedSystem.id);
        }
    });

    return {
        ...state,
        ids: [...state.ids, ...newIds],
        entities: nextEntities,
    };
}
